import { z } from 'zod';
import { optionTypeSchema } from '../../../helper';
import { outletSchema, IOutlet } from './outlet';
import { IPackLine } from '../packLine/packLine';
import { IPrintServer } from '../printServer/printServer';
import { IPrinter } from '../printer/printer';
import { IReport } from '../report/report';
import { ISite } from '../site/site';
import { IInventory } from '../inventory/inventory';

/**
 * Schema for validating outlet form data.
 * Extends the base outlet schema with form-specific validations.
 * Replaces ID fields with option type selections.
 *
 * @property {string} code - Outlet code (max 5 characters)
 * @property {string} [deviceMacAddress] - Optional MAC address of associated device (max 255 characters)
 * @property {OptionType} [packLine] - Pack line selection with label/value pair
 * @property {OptionType} [defaultPrintServer] - Optional default print server selection
 * @property {OptionType} [defaultPrinter] - Optional default printer selection
 * @property {OptionType} [defaultReport] - Optional default report selection
 * @property {OptionType} [defaultExporterSite] - Optional default exporter site selection
 * @property {OptionType} [defaultInventory] - Optional default inventory selection
 */
export const outletFormSchema = outletSchema.extend({
    packLine: optionTypeSchema.refine(val => val !== undefined, { message: 'Pack Line is required' }),
    defaultPrintServer: optionTypeSchema.optional(),
    defaultPrinter: optionTypeSchema.optional(),
    defaultReport: optionTypeSchema.optional(),
    defaultExporterSite: optionTypeSchema.optional(),
    defaultInventory: optionTypeSchema.optional(),
}).omit({
    packLineId: true,
    defaultPrintServerId: true,
    defaultPrinterId: true,
    defaultReportId: true,
    defaultExporterSiteId: true,
    defaultInventoryId: true,
});

/**
 * Default schema for outlet form initialization.
 * Extends the form schema by making required fields optional with defaults.
 *
 * @property {string} [code] - Optional outlet code
 * @property {OptionType} [packLine] - Optional pack line selection
 */
export const outletDefaultFormSchema = outletFormSchema.extend({
    code: z.string().optional().default(''),
    packLine: optionTypeSchema.optional(),
});

/**
 * TypeScript interface for outlet default form values.
 * Generated from the outlet default form schema for type safety.
 */
export type IOutletDefaultFormValues = z.infer<typeof outletDefaultFormSchema>;

/**
 * TypeScript interface representing Outlet form values.
 * Generated from the Zod schema for type safety.
 */
export type IOutletFormValues = z.infer<typeof outletFormSchema>;

/**
 * Factory function to create form values for an outlet.
 * Handles both new and existing outlets.
 *
 * @param {IOutlet} [outlet] - Optional existing outlet to populate form
 * @param {Array<IPackLine>} [packLines] - Optional pack lines
 * @param {Array<IPrintServer>} [printServers] - Optional print servers
 * @param {Array<IPrinter>} [printers] - Optional printers
 * @param {Array<IReport>} [reports] - Optional reports
 * @param {Array<ISite>} [sites] - Optional sites
 * @param {Array<IInventory>} [inventories] - Optional inventories
 * @returns {IOutletFormValues | IOutletDefaultFormValues} Form values with appropriate defaults
 */
export const OutletFormValues = (
    outlet ?: IOutlet,
    packLines ?: Array<IPackLine>,
    printServers ?: Array<IPrintServer>,
    printers ?: Array<IPrinter>,
    reports ?: Array<IReport>,
    sites ?: Array<ISite>,
    inventories ?: Array<IInventory>
) : IOutletFormValues | IOutletDefaultFormValues => {
    // Handle the default case (new outlet)
    if (!outlet) {
        return outletDefaultFormSchema.parse({});
    }

    const {
        packLineId,
        defaultPrintServerId,
        defaultPrinterId,
        defaultReportId,
        defaultExporterSiteId,
        defaultInventoryId,
        ...rest
    } = outlet;

    // Transform the IOutlet data into IOutletFormValues structure
    return outletFormSchema.parse({
        ...rest,
        packLine: packLineId ? {
            label: packLines?.find(x => x.id === packLineId)?.name ?? '',
            value: packLineId,
        } : {
            label: '',
            value: '',
        },
        defaultPrintServer: defaultPrintServerId ? {
            label: printServers?.find(x => x.id === defaultPrintServerId)?.name ?? '',
            value: defaultPrintServerId,
        } : undefined,
        defaultPrinter: defaultPrinterId ? {
            label: printers?.find(x => x.id === defaultPrinterId)?.name ?? '',
            value: defaultPrinterId,
        } : undefined,
        defaultReport: defaultReportId ? {
            label: reports?.find(x => x.id === defaultReportId)?.name ?? '',
            value: defaultReportId,
        } : undefined,
        defaultExporterSite: defaultExporterSiteId ? {
            label: sites?.find(x => x.id === defaultExporterSiteId)?.description ?? '',
            value: defaultExporterSiteId,
        } : undefined,
        defaultInventory: defaultInventoryId ? {
            label: inventories?.find(x => x.id === defaultInventoryId)?.code ?? '',
            value: defaultInventoryId,
        } : undefined,
    });
};

/**
 * Validates outlet form values.
 * Logs any validation errors to the console.
 *
 * @param {IOutletFormValues} formValues - The form values to validate
 * @returns {boolean} True if validation passes, false otherwise
 */
export const validateOutletForm = (formValues : IOutletFormValues) : boolean => {
    try {
        outletFormSchema.parse(formValues);
        return true;
    } catch (error) {
        if (error instanceof z.ZodError) {
            // eslint-disable-next-line no-console
            console.error('Outlet form validation failed:', error.errors);
        } else {
            // eslint-disable-next-line no-console
            console.error('An unexpected error occurred during validation:', error);
        }
        return false;
    }
};
